import { useEffect, useState } from "react";
import { toast } from "sonner";
import { EyeOff, Trash2, Plus, ShieldAlert } from "lucide-react";

import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";

export default function PrivateNumbers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/conversations/locks").then((r) => setRows(r.data)).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const unlock = async (waId) => {
    if (!window.confirm(`Unhide +${waId}? Staff will be able to see this conversation again.`)) return;
    try {
      await api.delete(`/conversations/${waId}/lock`);
      toast.success("Unhidden — visible to staff again");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="px-10 py-10">
      <header className="mb-8 flex items-end justify-between gap-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.24em] text-muted-foreground">Access Control</div>
          <h1 className="mt-2 font-display text-4xl font-black tracking-tight sm:text-5xl" data-testid="private-numbers-title">
            Private Numbers
          </h1>
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
            Numbers listed here are hidden from every staff account, even if the staff has access to the system that owns the chat. Only admins can view or reply to these conversations.
          </p>
        </div>
        <AddPrivateDialog onDone={load} />
      </header>

      <div className="mb-4 flex items-start gap-2 border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-amber-900 dark:text-amber-200">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
        <div>
          <div className="font-bold uppercase tracking-widest">Admin-only visibility</div>
          <p className="mt-0.5">Adding a number here immediately removes it from every staff inbox. Existing message history remains untouched — only visibility is affected.</p>
        </div>
      </div>

      <div className="border border-border">
        <Table>
          <TableHeader>
            <TableRow className="border-border">
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Mobile</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Note</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Hidden by</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Since</TableHead>
              <TableHead className="text-right text-[10px] font-bold uppercase tracking-[0.2em]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground">Loading…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="py-16 text-center text-muted-foreground">
                No private numbers yet. Click <span className="font-bold">Hide a number</span> above.
              </TableCell></TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.wa_id} className="border-border" data-testid={`private-row-${r.wa_id}`}>
                  <TableCell className="font-mono font-bold">+{r.wa_id}</TableCell>
                  <TableCell className="max-w-[280px] truncate text-xs text-muted-foreground">
                    {r.note || <span className="text-muted-foreground/70">—</span>}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {r.locked_by_mobile ? `+91 ${r.locked_by_mobile}` : "—"}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {r.locked_at?.slice(0, 16).replace("T", " ") || "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => unlock(r.wa_id)} data-testid={`unhide-${r.wa_id}`} className="h-8 rounded-sm text-xs">
                      <Trash2 className="mr-1 h-3.5 w-3.5 text-red-500" /> Unhide
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function AddPrivateDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [numbers, setNumbers] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const list = numbers
      .split(/[\n,;]/g)
      .map((s) => s.replace(/\D/g, ""))
      .filter((s) => s.length >= 10);
    if (list.length === 0) return toast.error("Enter at least one 10-digit mobile");
    setSaving(true);
    try {
      const r = await api.post("/conversations/locks/bulk", {
        mobiles: list,
        note: note.trim() || null,
      });
      const { added_count, skipped } = r.data;
      const alreadyHiddenCount = (skipped || []).filter((s) => s.reason === "already_locked").length;
      const invalidCount = (skipped || []).filter((s) => s.reason === "invalid").length;
      const parts = [`${added_count} hidden`];
      if (alreadyHiddenCount) parts.push(`${alreadyHiddenCount} already hidden`);
      if (invalidCount) parts.push(`${invalidCount} invalid`);
      toast.success(parts.join(", "));
      setOpen(false);
      setNumbers(""); setNote("");
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-sm" data-testid="add-private-btn">
          <EyeOff className="mr-2 h-4 w-4" /> Hide a number
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto rounded-sm sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">Hide numbers from staff</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em]">Mobile numbers</Label>
            <Textarea
              value={numbers}
              onChange={(e) => setNumbers(e.target.value)}
              placeholder="9855747777&#10;9463541220&#10;9998887776"
              rows={6}
              className="mt-1 rounded-sm font-mono text-sm"
              data-testid="private-numbers-input"
            />
            <p className="mt-1 text-[10px] text-muted-foreground">
              One 10-digit mobile per line. Comma or semicolon separators also work. India-only (+91 auto-added).
            </p>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em]">Note (optional)</Label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. VIP patients, senior management"
              className="mt-1 rounded-sm"
              data-testid="private-note-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-sm" data-testid="confirm-add-private-btn">
            {saving ? "Hiding…" : "Hide numbers"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
